// Focus helpers shared by the QuotationEditor hotkey infrastructure.
//
// The QuotationEditor sidebars live inside the Apryse web component's shadow DOM, so two
// things differ from normal DOM focus handling:
//   1. document.activeElement returns the shadow HOST (<apryse-webviewer>) when focus is
//      inside the viewer — getDeepActiveElement descends into shadow roots to find the
//      element actually focused.
//   2. Calling element.focus() from a hotkey handler does not reliably trigger
//      :focus-visible in every browser, so focusWithRing also toggles a class styled
//      identically to the keyboard focus ring (see hotkeyFocus.module.scss).

// Class toggled on the element we focus programmatically so the keyboard focus ring is
// guaranteed to appear even where :focus-visible would not fire.
export const KEYBOARD_FOCUS_CLASS = "is-keyboard-focused"

const FOCUSABLE_SELECTOR = [
	"a[href]",
	"button:not([disabled])",
	"input:not([disabled])",
	"textarea:not([disabled])",
	"select:not([disabled])",
	'[tabindex]:not([tabindex="-1"])',
	'[role="button"]:not([disabled])',
].join(",")

// True when the event target is somewhere the user is typing, so hotkeys must stay inert.
// Accepts the resolved (composed-path) target — see getEventTarget in the dispatcher.
export const isTypingTarget = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) return false
	const tag = target.tagName
	return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable === true
}

// The element actually focused, descending through any open shadow roots. Returns the
// inner element (e.g. a sidebar button) rather than the shadow host.
export const getDeepActiveElement = (): HTMLElement | null => {
	let active = document.activeElement as HTMLElement | null
	while (active?.shadowRoot?.activeElement) {
		active = active.shadowRoot.activeElement as HTMLElement
	}
	return active
}

// Visible, focusable descendants of root in DOM order. getClientRects() is empty for
// elements hidden via display:none (Ant collapse uses destroyOnHidden, so collapsed
// rows are removed entirely — this also covers any that linger hidden). Anything inside a
// [data-hotkey-skip] subtree is excluded from the Tab trap (e.g. the right sidebar's header
// nav / close buttons, which stay mouse-reachable but are not keyboard stops).
export const getFocusableElements = (root: HTMLElement): HTMLElement[] =>
	Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
		(el) => el.getClientRects().length > 0 && !el.closest("[data-hotkey-skip]"),
	)

// Focus an element and force a focus ring to show. focusVisible is honored where supported
// (Chromium); the class is the cross-browser guarantee. ringClass selects which ring style to
// apply (item ring by default, or the sidebar-focused state for the panel itself).
export const focusWithRing = (el: HTMLElement, ringClass: string = KEYBOARD_FOCUS_CLASS): void => {
	try {
		el.focus({ focusVisible: true } as FocusOptions)
	} catch {
		el.focus()
	}
	el.classList.add(ringClass)
	el.addEventListener("blur", () => el.classList.remove(ringClass), { once: true })
}

// Focus an element without adding a ring class — used for the sidebar panel itself, whose focused
// ring comes from a :has(:focus) rule on the panel container rather than the element's own class.
export const focusElement = (el: HTMLElement): void => {
	try {
		el.focus({ focusVisible: true } as FocusOptions)
	} catch {
		el.focus()
	}
}

// Focus the first focusable element inside root with the keyboard ring. Returns whether
// an element was focused.
export const focusFirstFocusable = (root: HTMLElement): boolean => {
	const [first] = getFocusableElements(root)
	if (!first) return false
	focusWithRing(first)
	return true
}

// Invoke attempt() once per animation frame until it returns true or maxFrames is reached.
// Apryse mounts its side panels asynchronously (outside React's commit), so a panel's focus
// target may not exist on the first frame after it opens — this retries until it does. Returns
// a cancel function. onGiveUp fires once if the loop exhausts maxFrames without attempt()
// succeeding (it does NOT fire when the caller cancels), letting callers clear pending state so a
// never-fulfilled focus request can't leak into a later cycle.
export const runWhenReady = (attempt: () => boolean, maxFrames = 30, onGiveUp?: () => void): (() => void) => {
	let raf = 0
	let frames = 0
	const tick = () => {
		if (attempt()) return
		if (frames++ >= maxFrames) {
			onGiveUp?.()
			return
		}
		raf = requestAnimationFrame(tick)
	}
	raf = requestAnimationFrame(tick)
	return () => cancelAnimationFrame(raf)
}
